import { brand } from '@/theme/foundations/brand';
import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  LinearProgress,
  Chip,
  Divider,
  Avatar,
  Paper,
  Button,
  List,
  ListItem,
  ListItemText,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  CloudQueue,
  Timeline,
  Security,
  Schedule,
  CheckCircle,
  Info,
  StarBorder,
} from '@mui/icons-material';
import { useNotification } from '../../notification/NotificationContext';
import { useGetUserSubscriptionQuery } from '@/generated/graphql';
import { formatFileSize, formatDateShort } from '@/common/utils/fileUtils';
import {
  getSubscriptionTierColor,
  getSubscriptionStatusColor,
} from '@/common/utils/statusUtils';
import { getPlanFeatures } from '@/constants/subscriptionTiers';
import { TierFeatures } from '../../subscription/TierFeatures';
import { LoadingSpinner } from '@/common/components/loading/LoadingSpinner';
import { SubscriptionTier } from '@/common/models/enums';
import DialogWindow from '@/common/components/_misc/DialogWindow';
import { FaDiscord } from 'react-icons/fa';

const SubscriptionDetails: React.FC = () => {
  const { showError } = useNotification();
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);

  const { data, loading, error } = useGetUserSubscriptionQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (error) {
      showError('Failed to load subscription data');
    }
  }, [error, showError]);

  if (loading) {
    return <LoadingSpinner message="Loading subscription details..." />;
  }

  if (error) {
    return (
      <Box>
        <Typography color="error">
          Error loading subscription: {error.message}
        </Typography>
      </Box>
    );
  }

  if (!data?.user?.subscription) {
    return (
      <Box>
        <Typography>No subscription data available</Typography>
      </Box>
    );
  }

  const { subscription } = data.user;

  return (
    <Box>
      <Typography variant="h5" gutterBottom>
        Subscription Details
      </Typography>

      <Box display="flex" flexDirection="column" gap={3}>
        {/* Top Row - Plan Info and Features */}
        <Box display="flex" flexDirection={{ xs: 'column', md: 'row' }} gap={3}>
          {/* Subscription Overview */}
          <Box flex={1}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar
                    sx={{
                      bgcolor:
                        subscription.tier.toLowerCase() === SubscriptionTier.PRO
                          ? 'primary.main'
                          : 'secondary.main',
                      width: 48,
                      height: 48,
                    }}
                  >
                    {subscription.tier.toLowerCase() ===
                    SubscriptionTier.FREE ? (
                      <StarBorder />
                    ) : subscription.tier.toLowerCase() ===
                      SubscriptionTier.PRO ? (
                      <Timeline />
                    ) : (
                      <Security />
                    )}
                  </Avatar>
                  <Box>
                    <Typography variant="h6" gutterBottom sx={{ mb: 0.5 }}>
                      Plan Information
                    </Typography>
                    <Box display="flex" gap={1}>
                      <Chip
                        label={subscription.tier.toUpperCase()}
                        color={getSubscriptionTierColor(subscription.tier)}
                        variant="filled"
                        size="small"
                      />
                      <Chip
                        label={subscription.status.toUpperCase()}
                        color={getSubscriptionStatusColor(subscription.status)}
                        variant="outlined"
                        size="small"
                      />
                    </Box>
                  </Box>
                </Box>

                <Paper
                  variant="outlined"
                  sx={{ p: 2, bgcolor: 'background.default' }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={1}>
                    <CheckCircle
                      color={subscription.isActive ? 'success' : 'disabled'}
                      fontSize="small"
                    />
                    <Typography variant="body2" fontWeight="medium">
                      Status: {subscription.isActive ? 'Active' : 'Inactive'}
                    </Typography>
                  </Box>

                  {subscription.currentPeriodStart &&
                    subscription.currentPeriodEnd && (
                      <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <Schedule color="action" fontSize="small" />
                        <Typography variant="body2" color="textSecondary">
                          Billing Period:{' '}
                          {formatDateShort(subscription.currentPeriodStart)} -{' '}
                          {formatDateShort(subscription.currentPeriodEnd)}
                        </Typography>
                      </Box>
                    )}

                  {subscription.daysUntilExpiry && (
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Info color="primary" fontSize="small" />
                      <Typography variant="body2" color="primary">
                        Expires in {subscription.daysUntilExpiry} days
                      </Typography>
                    </Box>
                  )}

                  {subscription.trialStart && subscription.trialEnd && (
                    <Box display="flex" alignItems="center" gap={1}>
                      <Schedule color="info" fontSize="small" />
                      <Typography variant="body2" color="info.main">
                        Trial: {formatDateShort(subscription.trialStart)} -{' '}
                        {formatDateShort(subscription.trialEnd)}
                      </Typography>
                    </Box>
                  )}

                  {subscription.stripeCustomerId && (
                    <Box mt={1} pt={1} borderTop={1} borderColor="divider">
                      <Typography variant="caption" color="textSecondary">
                        Customer ID: {subscription.stripeCustomerId}
                      </Typography>
                    </Box>
                  )}
                </Paper>
              </CardContent>
            </Card>
          </Box>

          {/* Features */}
          <Box flex={1}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" alignItems="center" gap={2} mb={3}>
                  <Avatar
                    sx={{
                      bgcolor: 'success.main',
                      width: 48,
                      height: 48,
                    }}
                  >
                    <CloudQueue />
                  </Avatar>
                  <Typography variant="h6">Plan Features</Typography>
                </Box>

                <TierFeatures
                  features={getPlanFeatures(
                    subscription.tier,
                    subscription.features.storageBytes
                  )}
                />

                {subscription.tier.toLowerCase() === SubscriptionTier.FREE && (
                  <Button
                    variant="contained"
                    color="success"
                    fullWidth
                    size="large"
                    sx={{
                      mt: 2,
                      fontWeight: 800,
                      borderRadius: 2,
                    }}
                    onClick={() => setUpgradeDialogOpen(true)}
                  >
                    GET MORE BETA STORAGE FREE !!!
                  </Button>
                )}
              </CardContent>
            </Card>
          </Box>
        </Box>

        {/* Storage Usage */}
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Storage Usage
            </Typography>

            <Box mb={2}>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="body2">
                  {formatFileSize(subscription.storageUsed)} of{' '}
                  {formatFileSize(subscription.storageLimit)} used
                </Typography>
                <Typography variant="body2" fontWeight="medium">
                  {subscription.storagePercentageUsed.toFixed(1)}%
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={Math.min(subscription.storagePercentageUsed, 100)}
                color={
                  subscription.storagePercentageUsed > 90
                    ? 'error'
                    : subscription.storagePercentageUsed > 80
                      ? 'warning'
                      : 'primary'
                }
                sx={{ height: 8, borderRadius: 4 }}
              />

              {/* Available space indicator */}
              <Box mt={1}>
                <Typography variant="body2" color="textSecondary">
                  {formatFileSize(
                    subscription.storageLimit - subscription.storageUsed
                  )}{' '}
                  available
                  {subscription.projectBreakdown.length > 0 && (
                    <>
                      {' '}
                      • {subscription.projectBreakdown.length} project
                      {subscription.projectBreakdown.length !== 1 ? 's' : ''}
                    </>
                  )}
                </Typography>
              </Box>
            </Box>

            {subscription.storagePercentageUsed > 90 && (
              <Typography variant="body2" color="error.main" gutterBottom>
                🚨 Storage almost full! Please free up space or upgrade your
                plan.
              </Typography>
            )}
            {subscription.storagePercentageUsed > 80 &&
              subscription.storagePercentageUsed <= 90 && (
                <Typography variant="body2" color="warning.main" gutterBottom>
                  ⚠️ You&apos;re running low on storage space. Consider
                  upgrading your plan.
                </Typography>
              )}
          </CardContent>
        </Card>

        {/* Project Breakdown */}
        {subscription.projectBreakdown.length > 0 && (
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Storage by Project
                <Typography
                  component="span"
                  variant="body2"
                  color="textSecondary"
                  ml={1}
                >
                  ({subscription.projectBreakdown.length} project
                  {subscription.projectBreakdown.length !== 1 ? 's' : ''})
                </Typography>
              </Typography>

              <List disablePadding>
                {[...subscription.projectBreakdown]
                  .sort((a, b) => b.storageUsed - a.storageUsed)
                  .map((project, index) => {
                    const projectPercentage =
                      (project.storageUsed / subscription.storageLimit) * 100;
                    const projectOfTotalUsed =
                      (project.storageUsed / subscription.storageUsed) * 100;

                    return (
                      <React.Fragment key={project.projectId}>
                        <ListItem sx={{ px: 0 }}>
                          <ListItemText
                            primary={
                              <Box
                                display="flex"
                                justifyContent="space-between"
                                alignItems="center"
                              >
                                <Typography variant="body1" fontWeight="medium">
                                  {project.projectName}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  color="textSecondary"
                                >
                                  {projectOfTotalUsed.toFixed(1)}% of total
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  mt={0.5}
                                >
                                  <Typography variant="body2" component="span">
                                    {formatFileSize(project.storageUsed)} •{' '}
                                    {project.fileCount} file
                                    {project.fileCount !== 1 ? 's' : ''}
                                  </Typography>
                                  <Typography
                                    variant="body2"
                                    color="textSecondary"
                                  >
                                    {projectPercentage.toFixed(2)}% of limit
                                  </Typography>
                                </Box>
                                <Box mt={0.5}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={Math.min(projectPercentage, 100)}
                                    color={
                                      projectPercentage > 50
                                        ? 'warning'
                                        : 'primary'
                                    }
                                    sx={{ height: 6, borderRadius: 3 }}
                                  />
                                </Box>
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < subscription.projectBreakdown.length - 1 && (
                          <Divider variant="middle" />
                        )}
                      </React.Fragment>
                    );
                  })}
              </List>
            </CardContent>
          </Card>
        )}
      </Box>

      <DialogWindow
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
        title="Get More Beta Storage"
        fullWidth
        maxWidth="sm"
        hideExpand
      >
        <DialogContent sx={{ p: 0 }}>
          <Box
            sx={{
              px: { xs: 2.5, sm: 3 },
              pt: { xs: 2.5, sm: 3 },
              pb: 2,
              background: brand.discord.glow,
            }}
          >
            <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
              <Avatar
                sx={{
                  width: 44,
                  height: 44,
                  bgcolor: brand.discord.tint,
                  color: brand.discord.main,
                }}
              >
                <FaDiscord size={24} />
              </Avatar>
              <Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 700, lineHeight: 1.2 }}
                >
                  Request Extra Storage
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Free for active beta testers
                </Typography>
              </Box>
            </Box>

            <Typography variant="body1" sx={{ mb: 2 }}>
              Need more space? Join our Discord server and post a request for
              extra storage.
            </Typography>

            <Box
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                px: 1.5,
                py: 1,
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="body2" color="text.secondary">
                Our team reviews requests quickly and can raise your limit
                during beta.
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: { xs: 2.5, sm: 3 }, pb: 3, pt: 1.5 }}>
          <Button
            onClick={() => setUpgradeDialogOpen(false)}
            sx={{ fontWeight: 600 }}
          >
            Not now
          </Button>
          <Button
            component="a"
            href="https://discord.gg/srd3rH6WYU"
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            startIcon={<FaDiscord />}
            sx={{
              ml: 'auto',
              fontWeight: 700,
              borderRadius: 2,
              px: 2.25,
              background: brand.discord.fill,
              '&:hover': {
                background: brand.discord.hover,
              },
            }}
          >
            Join Discord
          </Button>
        </DialogActions>
      </DialogWindow>
    </Box>
  );
};

export default SubscriptionDetails;
